import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { CarouselProvider, Slider, Slide, ButtonBack, ButtonNext } from 'pure-react-carousel';
import 'pure-react-carousel/dist/react-carousel.es.css';
import { Divider } from '@mui/material';
import { useGetCafeDetailsQuery } from '../../../../store/api/cafeApi';
import { IsOpen, minutesOfDay } from '../../../../utils/time';
import CafeCardsItem from './CafeCardsItem';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Link from '@mui/material/Link';
import { DEFAULT_CAFE_SECTION_NUM } from '../../../../utils/constant';
import Loading from '../../../../components/ui/Loading';

const CafeCardsContainer = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '0 30px',
  minHeight: '360px',
  margin: '20px auto',
  '& .carousel__back-button svg': {
    transform: 'rotate(-180deg)',
  },
  '& .carousel__next-button, & .carousel__back-button': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      width: '12px',
      height: '18px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    paddingLeft: '0 24px',
  },
}));

const TitleContainer = styled('div')({
  width: '100%',
  overflow: 'hidden',
  p: {
    fontFamily: 'Fjalla One',
    fontWeight: '600',
    fontSize: '22px',
    lineHeight: '28px',
    margin: '10px 0 10px 0',
  },
});

const TitleDividerContainer = styled(Divider)(({ theme }) => ({
  width: '80px',

  '&:before,&:after': {
    borderTop: '2px solid #AA9178',
    height: '6px',
  },

  '&>span': {
    margin: '0',
    color: '#AA9178',
    fontSize: '8px',
    lineHeight: '8px',
    [theme.breakpoints.down('sm')]: {
      padding: '0 6px',
    },
  },
}));

const CafeCardContainer = styled('div')({
  position: 'relative',
  width: '100%',
  margin: '15px auto 0',
});

const StyledSlide = styled(Slide)({
  '& .focusRing___1airF.carousel__slide-focus-ring': {
    outline: 'none',
  },
});

const NextBtn = styled(ButtonNext)({
  position: 'absolute',
  width: '25px',
  height: '25px',
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(217, 217, 217, 0.65)',
  boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.25)',
  color: 'rgba(0, 0, 0, 0.54)',
  cursor: 'pointer',
  right: '0px',
  top: '40%',
});

const PrevBtn = styled(ButtonBack)({
  position: 'absolute',
  width: '25px',
  height: '25px',
  borderRadius: '50%',
  backgroundColor: '#ffffff',
  border: '1px solid rgba(217, 217, 217, 0.65)',
  boxShadow: '1px 1px 2px rgba(0, 0, 0, 0.25)',
  color: 'rgba(0, 0, 0, 0.54)',
  cursor: 'pointer',
  left: '0px',
  top: '40%',
});

const RatingAvg = (data) => {
  if (!data.length) {
    return '—';
  }
  let total = 0;
  data.forEach((item) => {
    total += item.ratingScore;
  });
  return (total / data.length).toFixed(1);
};
const CafeAddress = (data) => {
  const { country, state, city, streetName, streetNumber } = data;
  return `${streetNumber} ${streetName} , ${city} ${state} ${country}`;
};
const openTime = (data) => {
  if (!data) {
    return {
      status: 'Close',
      closeTime: '',
    };
  }
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const tDate = new Date();
  const tDay = tDate.getDay();
  const tTime = tDate.getHours() + ':' + tDate.getMinutes();
  const OpenTimeArray = Object.values(data).filter((item) => weekday.indexOf(item.day) > -1);
  const closeTime =
    minutesOfDay(tTime) < minutesOfDay(OpenTimeArray[tDay].openTime) &&
    `until ${OpenTimeArray[tDay].closeTime} ${weekday[tDay]}`;
  return {
    status: IsOpen(OpenTimeArray) ? 'Open' : 'Close',
    closeTime,
  };
};
const CafeCards = () => {
  const [width, setWidth] = useState(window.innerWidth);
  const [slidesNum, setSlidesNum] = useState(
    parseInt((width - 50 - (width - 50) * 0.03 * 2) / 295)
  );

  useEffect(() => {
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  const updateDimensions = () => {
    setWidth(window.innerWidth);

    setSlidesNum(parseInt((window.innerWidth - 50 - (window.innerWidth - 50) * 0.03 * 2) / 295));
  };
  const { data: cafeInfo, isLoading } = useGetCafeDetailsQuery(DEFAULT_CAFE_SECTION_NUM);
  if (!cafeInfo) return <Loading />;

  let cafes = [];
  if (!isLoading) {
    const temp = !isLoading && cafeInfo.data;
    cafes = temp.map((item) => {
      return {
        id: item._id,
        rate: RatingAvg(item.cafeRatings),
        like: item.likedUsers.length,
        posts: item.relatedNotes.length,
        photo: item.attachments[0] && item.attachments[0].url,
        alt: 'cafe photo',
        status: openTime(item.tradingHours).status,
        closeTime: openTime(item.tradingHours).closeTime,
        cafeName: item.cafeName,
        cafeAddress: CafeAddress(item.cafeAddress),
      };
    });
  }

  const linkUrl = `/cafe/`;

  return (
    <CafeCardsContainer>
      <TitleContainer>
        <p>Top Trending Cafe</p>
        <TitleDividerContainer>♦</TitleDividerContainer>
      </TitleContainer>
      <CafeCardContainer>
        <CarouselProvider
          naturalSlideWidth={295}
          naturalSlideHeight={250}
          totalSlides={cafes.length}
          visibleSlides={slidesNum}
          infinite={true}
          dragEnabled={false}
          isIntrinsicHeight
        >
          <Slider moveThreshold={0.6}>
            {cafes.map((cafe) => (
              <StyledSlide
                classNameHidden="hidden"
                classNameVisible="visible"
                key={cafe.id}
                style={{ paddingBottom: '5px', paddingTop: '3px' }}
              >
                <Link href={linkUrl + cafe.id} underline="none">
                  <CafeCardsItem cafe={cafe} key={cafe.id} />
                </Link>
              </StyledSlide>
            ))}
          </Slider>
          <PrevBtn>
            <ArrowForwardIosIcon />
          </PrevBtn>
          <NextBtn>
            <ArrowForwardIosIcon />
          </NextBtn>
        </CarouselProvider>
      </CafeCardContainer>
    </CafeCardsContainer>
  );
};

export default CafeCards;
